import React, { useEffect, useRef, useState } from "react";
import { useUI } from "../../context/UIContext";
import {
  getAppDeployInfo,
  getDeployedApps,
  getDeployHistory,
  deployFolder,
  startDeployedApp,
} from "../../api";
import { fmtVnTime } from "../../utils/format";
import ShellModal from "../modals/ShellModal";

const portEnv = (port) => (port ? `set "PORT=${port}" && ` : "");
const httpsEnv = (hp) => (hp ? `set "HTTPS_PORT=${hp}" && ` : "");
const env2 = (port, hp) => portEnv(port) + httpsEnv(hp);
const PRESETS = {
  appjs: {
    label: "Chạy file app.js via PM2",
    gen: (n, port, hp) =>
      `npm install --omit=dev && (pm2 delete ${n} & ${env2(port, hp)}pm2 start app.js --name ${n}) && pm2 save`,
  },
  npmstart: {
    label: "Theo package.json (npm start)",
    gen: (n, port, hp) =>
      `npm install --omit=dev && (pm2 delete ${n} & ${env2(port, hp)}pm2 start npm --name ${n} -- start) && pm2 save`,
  },
  custom: { label: "Tuỳ chỉnh (tự nhập)", gen: () => "" },
};

export default function DeployView({ servers, isAdmin }) {
  const { toast, openModal } = useUI();
  const list = Object.values(servers);
  const firstConnected = list.find((s) => s.isConnected) || list[0];

  const [code, setCode] = useState(firstConnected?.ServerCode || "");
  const [cfg, setCfg] = useState({
    TargetDir: "C:\\NodeApp",
    StartCommand: "",
    Port: "",
    HttpsPort: "",
  });
  const [files, setFiles] = useState([]);
  const [folderName, setFolderName] = useState("");
  const [preset, setPreset] = useState("appjs");
  const [history, setHistory] = useState([]);
  const [deployedApps, setDeployedApps] = useState([]);
  const [deploying, setDeploying] = useState(false);
  const fileRef = useRef(null);

  const loadAll = (sc) => {
    if (!sc) return;
    getDeployHistory(sc).then(setHistory).catch(() => setHistory([]));
    getDeployedApps(sc).then(setDeployedApps).catch(() => setDeployedApps([]));
  };

  const prefillFrom = (sc, appName) => {
    if (!sc || !appName) return;
    getAppDeployInfo(sc, appName).then(({ ok, data }) => {
      if (ok && data.found) {
        setCfg({
          TargetDir: data.TargetDir || "",
          StartCommand: data.StartCommand || "",
          Port: data.Port != null ? String(data.Port) : "",
          HttpsPort: data.HttpsPort != null ? String(data.HttpsPort) : "",
        });
      }
    });
  };

  useEffect(() => {
    loadAll(code);
  }, [code]); // eslint-disable-line

  useEffect(() => {
    if (fileRef.current) {
      fileRef.current.setAttribute("webkitdirectory", "");
      fileRef.current.setAttribute("directory", "");
    }
  }, []);

  useEffect(() => {
    if (preset && preset !== "custom" && PRESETS[preset]) {
      setCfg((c) => ({
        ...c,
        StartCommand: PRESETS[preset].gen(folderName || "app", c.Port, c.HttpsPort),
      }));
    }
  }, [preset, folderName, cfg.Port, cfg.HttpsPort]); // eslint-disable-line

  if (!isAdmin) {
    return (
      <div className="empty-state" style={{ marginTop: 40 }}>
        🔒 Chức năng Triển khai chỉ dành cho Admin.
      </div>
    );
  }

  const set = (k) => (e) => setCfg((c) => ({ ...c, [k]: e.target.value }));

  const onPickFolder = (e) => {
    const arr = Array.from(e.target.files || []);
    setFiles(arr);
    const fn = arr.length
      ? (arr[0].webkitRelativePath || "").split("/")[0]
      : "";
    setFolderName(fn);
    if (!fn) return;
    const known = deployedApps.find((a) => a.AppName === fn);
    if (known) {
      prefillFrom(code, fn);
      setPreset("custom");
    } else {
      setPreset(preset === "custom" ? "appjs" : preset);
    }
  };

  const onPickPreset = (key) => setPreset(key);

  const appPath =
    cfg.TargetDir && folderName
      ? `${cfg.TargetDir.replace(/[\\/]+$/, "")}\\${folderName}`
      : cfg.TargetDir;

  const onDeploy = async () => {
    if (!code) return toast("Chọn máy chủ", "err");
    if (!files.length) return toast("Chọn thư mục để đẩy", "err");
    setDeploying(true);
    toast(`Đang nén & đẩy ${files.length} file…`, "ok", 4000);
    try {
      const { ok, status, data } = await deployFolder(code, files, {
        targetDir: cfg.TargetDir,
        startCmd: cfg.StartCommand,
        port: cfg.Port,
        httpsPort: cfg.HttpsPort,
      });
      if (ok) {
        toast(data.message || "Deploy thành công ✅", "ok", 6000);
      } else if (status === 503) {
        toast(data.message || "Máy chủ chưa kết nối", "warn", 5000);
      } else {
        toast(data.message || "Deploy thất bại", "err", 7000);
      }
      loadAll(code);
    } catch {
      toast("Không kết nối được BackendNode", "err");
    } finally {
      setDeploying(false);
    }
  };

  const [restarting, setRestarting] = useState("");
  const onRestart = async (appName) => {
    setRestarting(appName);
    toast(`Đang khởi động lại ${appName}…`, "ok", 4000);
    try {
      const { ok, data } = await startDeployedApp(code, appName);
      toast(
        ok ? data.message || `Đã khởi động lại ${appName} ✅` : data.message || "Lỗi",
        ok ? "ok" : "err",
        ok ? 4000 : 6000
      );
    } catch {
      toast("Không kết nối được BackendNode", "err");
    } finally {
      setRestarting("");
    }
  };

  const openShell = () => {
    if (!code) return toast("Chọn máy chủ", "err");
    openModal(`PowerShell · ${code}`, <ShellModal serverCode={code} />);
  };

  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "minmax(0, 1.15fr) minmax(0, 1fr)",
      gap: "16px",
      alignItems: "start",
    }} className="deploy-grid-layout">
      
      {/* Left Column: Deploy Form */}
      <div style={{
        borderRadius: "14px",
        background: "var(--surface)",
        border: "1px solid var(--border)",
        overflow: "hidden",
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 18px",
          borderBottom: "1px solid var(--border)",
        }}>
          <span style={{ fontSize: "14px", fontWeight: 700, color: "var(--text)" }}>Deploy NodeApp</span>
          <button
            onClick={openShell}
            style={{
              padding: "0 12px",
              height: "32px",
              borderRadius: "8px",
              background: "var(--surface2)",
              border: "1px solid var(--border2)",
              color: "var(--dim)",
              fontSize: "12.5px",
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              transition: "all 0.2s ease",
            }}
            className="deploy-act-btn"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="4 17 10 11 4 5"></polyline>
              <line x1="12" y1="19" x2="20" y2="19"></line>
            </svg>
            Run command
          </button>
        </div>
        
        <div style={{ padding: "18px", display: "flex", flexDirection: "column", gap: "15px" }}>
          {/* Target server */}
          <div>
            <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--dim)", marginBottom: "7px" }}>Target server *</div>
            <select
              style={selectStyle}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            >
              {list.length === 0 && <option value="">— Chưa có máy chủ —</option>}
              {list.map((s) => (
                <option key={s.ServerCode} value={s.ServerCode}>
                  {s.ServerCode} {s.ServerName && s.ServerName !== s.ServerCode ? ` · ${s.ServerName}` : ""} {!s.isConnected ? " (offline)" : ""}
                </option>
              ))}
            </select>
          </div>

          {/* Target directory */}
          <div>
            <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--dim)", marginBottom: "7px" }}>Target directory</div>
            <input
              className="field-inp"
              value={cfg.TargetDir}
              placeholder="C:\NodeApp"
              onChange={set("TargetDir")}
              style={{ marginBottom: "0px", fontFamily: "var(--font-mono)", fontSize: "13.5px" }}
            />
          </div>

          {/* Ports */}
          <div style={{ display: "flex", gap: "12px" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--dim)", marginBottom: "7px" }}>HTTP port</div>
              <input
                className="field-inp"
                type="number"
                value={cfg.Port}
                placeholder="4000"
                onChange={set("Port")}
                style={{ marginBottom: "0px", fontFamily: "var(--font-mono)", fontSize: "13.5px" }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--dim)", marginBottom: "7px" }}>HTTPS port</div>
              <input
                className="field-inp"
                type="number"
                value={cfg.HttpsPort}
                placeholder="4001"
                onChange={set("HttpsPort")}
                style={{ marginBottom: "0px", fontFamily: "var(--font-mono)", fontSize: "13.5px" }}
              />
            </div>
          </div>

          {/* Start Command */}
          <div>
            <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--dim)", marginBottom: "7px" }}>Start command</div>
            <select
              style={{ ...selectStyle, marginBottom: "8px" }}
              value={preset}
              onChange={(e) => onPickPreset(e.target.value)}
            >
              {Object.entries(PRESETS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </select>
            <textarea
              className="field-inp"
              rows={2}
              value={cfg.StartCommand}
              placeholder="Chọn mẫu ở trên, hoặc tự gõ lệnh tại đây…"
              onChange={(e) => {
                setPreset("custom");
                setCfg((c) => ({ ...c, StartCommand: e.target.value }));
              }}
              style={{
                resize: "vertical",
                fontFamily: "var(--font-mono)",
                padding: "11px 13px",
                minHeight: "62px",
                marginBottom: "0px",
                fontSize: "12.5px",
                color: "var(--dim)",
              }}
            />
          </div>

          <div style={{ height: "1px", background: "var(--border)" }}></div>

          {/* NodeApp folder */}
          <div>
            <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--dim)", marginBottom: "7px" }}>NodeApp folder</div>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "14px 16px",
              borderRadius: "10px",
              border: "1.5px dashed var(--border2)",
              background: "var(--surface2)",
              position: "relative",
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--faint)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2z"></path>
              </svg>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "12.5px", color: "var(--text)", fontWeight: 500 }}>
                  {folderName ? `${folderName}/ · ${files.length} tệp` : "Chưa chọn thư mục"}
                </div>
                <div style={{ fontSize: "11px", color: "var(--faint)", fontFamily: "var(--font-mono)" }}>
                  {appPath || "Vui lòng chọn thư mục chứa NodeApp để đẩy"}
                </div>
              </div>
              <input
                ref={fileRef}
                type="file"
                multiple
                onChange={onPickFolder}
                style={{
                  position: "absolute",
                  inset: 0,
                  opacity: 0,
                  cursor: "pointer",
                  width: "100%",
                  height: "100%",
                }}
              />
            </div>
          </div>

          <button
            onClick={onDeploy}
            disabled={deploying}
            style={{
              width: "100%",
              height: "44px",
              borderRadius: "11px",
              background: "var(--primary)",
              border: "none",
              color: "#fff",
              fontSize: "14px",
              fontWeight: 700,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              boxShadow: "0 8px 20px -8px var(--primary-soft)",
              transition: "all 0.2s ease",
            }}
            className="deploy-run-btn"
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"></path>
              <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"></path>
            </svg>
            {deploying ? "Đang triển khai..." : "Deploy & run"}
          </button>
        </div>
      </div>

      {/* Right Column: Deployed Apps + History */}
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {/* Deployed Apps Panel */}
        <div style={{
          borderRadius: "14px",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          overflow: "hidden",
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 18px",
            borderBottom: "1px solid var(--border)",
          }}>
            <div style={{ fontSize: "14px", fontWeight: 700, color: "var(--text)" }}>Deployed apps</div>
            <span style={{ fontSize: "11.5px", color: "var(--faint)", fontFamily: "var(--font-mono)" }}>
              {deployedApps.length} apps
            </span>
          </div>
          <div>
            {deployedApps.length === 0 ? (
              <div style={{
                padding: "18px",
                color: "var(--faint)",
                fontSize: "12.5px",
                fontFamily: "var(--font-mono)",
                textAlign: "center",
              }}>
                Chưa có app nào được deploy.
              </div>
            ) : (
              deployedApps.map((a, idx) => (
                <div
                  key={a.AppName}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "13px 18px",
                    borderBottom: idx === deployedApps.length - 1 ? "none" : "1px solid var(--border)",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "13.5px", fontWeight: 600, color: "var(--text)" }}>{a.AppName}</div>
                    <div style={{ fontSize: "11px", color: "var(--faint)", fontFamily: "var(--font-mono)" }}>
                      {a.Port ? `:${a.Port}` : "—"} {a.HttpsPort ? `/ ${a.HttpsPort}` : ""} · {a.FolderPathNode}
                    </div>
                  </div>
                  <button
                    disabled={restarting === a.AppName}
                    onClick={() => onRestart(a.AppName)}
                    style={{
                      padding: "6px 12px",
                      borderRadius: "8px",
                      background: "var(--surface2)",
                      border: "1px solid var(--border2)",
                      color: "var(--text)",
                      fontSize: "12px",
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                    className="deployed-app-restart-btn"
                  >
                    ↻ Restart
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Deploy History Panel */}
        <div style={{
          borderRadius: "14px",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          overflow: "hidden",
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 18px",
            borderBottom: "1px solid var(--border)",
          }}>
            <div style={{ fontSize: "14px", fontWeight: 700, color: "var(--text)" }}>Deploy history</div>
            <span style={{ fontSize: "11.5px", color: "var(--faint)", fontFamily: "var(--font-mono)" }}>
              {history.length} deploys
            </span>
          </div>
          <div style={{ overflowX: "auto" }} className="scroll">
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "420px" }}>
              <thead>
                <tr>
                  <th style={miniThStyle}>Time</th>
                  <th style={miniThStyle}>Status</th>
                  <th style={miniThStyle}>Files</th>
                  <th style={miniThStyle}>By</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{
                      textAlign: "center",
                      padding: "18px",
                      color: "var(--faint)",
                      fontFamily: "var(--font-mono)",
                      fontSize: "12px",
                    }}>
                      Chưa có lịch sử.
                    </td>
                  </tr>
                ) : (
                  history.slice(0, 5).map((h) => (
                    <tr key={h.DeployId}>
                      <td style={miniTdStyle}>{fmtVnTime(h.StartedDate).split(" ")[1] + " " + fmtVnTime(h.StartedDate).split(" ")[0]}</td>
                      <td style={miniTdStyle}>
                        <span style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: "12px",
                          fontWeight: 700,
                          color: h.Status === "success" ? "var(--green)" : "var(--red)",
                        }}>
                          {h.Status === "success" ? "● success" : "✕ failed"}
                        </span>
                      </td>
                      <td style={miniTdStyle}>{h.FileCount}</td>
                      <td style={{ ...miniTdStyle, color: "var(--dim)" }}>{h.ByUser}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <style>{`
        .deploy-act-btn:hover {
          color: var(--text) !important;
          border-color: var(--dim) !important;
        }
        .deploy-run-btn:hover:not(:disabled) {
          filter: brightness(1.07);
        }
        .deployed-app-restart-btn:hover {
          border-color: var(--primary) !important;
          color: var(--primary) !important;
        }
        @media (max-width: 1024px) {
          .deploy-grid-layout {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

const selectStyle = {
  width: "100%",
  height: "42px",
  padding: "0 13px",
  borderRadius: "10px",
  background: "var(--surface2)",
  border: "1px solid var(--border)",
  color: "var(--text)",
  fontSize: "13.5px",
  outline: "none",
  boxSizing: "border-box",
  cursor: "pointer",
};

const miniThStyle = {
  textAlign: "left",
  fontSize: "10.5px",
  fontWeight: "700",
  letterSpacing: ".8px",
  textTransform: "uppercase",
  color: "var(--faint)",
  padding: "10px 18px",
  background: "var(--surface2)",
  borderBottom: "1px solid var(--border)",
};

const miniTdStyle = {
  padding: "11px 18px",
  borderBottom: "1px solid var(--border)",
  fontSize: "12px",
  fontFamily: "var(--font-mono)",
  color: "var(--dim)",
  verticalAlign: "middle",
};
